import { useQuery } from 'urql';
import { useMemo } from 'react';
import { getMetaAdCampaignsByAccount } from '../graphql';
import type { MetaAdCampaign, CampaignsByAccountData } from './types';

// Type guard to check if a campaign is non-nullable
const isNonNullableCampaign = <T extends { id?: string | null }>(campaign: T | null | undefined): campaign is T => {
  return campaign !== null && campaign !== undefined && campaign.id != null;
};

// Type for campaigns with account information
export interface AccountCampaign extends MetaAdCampaign {
  accountId: string;
  accountName?: string;
}

export const useCampaignsByAccount = ({
  clientId,
  accountIds,
  pause = false,
}: {
  clientId: string;
  accountIds: string[];
  pause?: boolean;
}) => {
  const [{ data, fetching, error }] = useQuery<CampaignsByAccountData>({
    query: getMetaAdCampaignsByAccount,
    variables: { clientId, accountIds },
    pause: pause || !clientId || accountIds.length === 0,
  });

  // Memoized flattening of campaigns from all accounts
  const allCampaigns = useMemo((): AccountCampaign[] => {
    return (
      data?.client?.metaAdAccounts?.flatMap(
        (account) =>
          account?.metaAdCampaigns?.map((campaign) => ({
            ...campaign,
            accountId: account.id,
            accountName: account.name,
          })) || [],
      ) || []
    ).filter(isNonNullableCampaign);
  }, [data?.client?.metaAdAccounts]);

  // Memoized accounts with campaigns
  const accountsWithCampaigns = useMemo(() => {
    return data?.client?.metaAdAccounts?.filter(isNonNullableCampaign) || [];
  }, [data?.client?.metaAdAccounts]);

  return {
    data,
    fetching,
    error,
    campaigns: allCampaigns,
    accountsWithCampaigns,
  };
};
